import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Globe } from 'react-feather'
import { useOnClickOutside } from '../../hooks/useOnClickOutside'

const LanguageSelectorContainer = styled.div`
  position: relative;
  display: inline-block;
  margin-left: 10px;
`

const LanguageButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background-color: ${({ theme }) => theme.bg3};
  border: none;
  border-radius: 8px;
  color: ${({ theme }) => theme.text1};
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: fit-content;

  &:hover {
    background-color: ${({ theme }) => theme.bg4};
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    padding: 10px 12px;
  }
`

const LanguageMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background-color: ${({ theme }) => theme.bg1};
  border: 1px solid ${({ theme }) => theme.bg3};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 200px;
  width: max-content;
  max-height: 400px;
  overflow-y: auto;
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.bg3};
    border-radius: 2px;
  }
`

const LanguageItem = styled.div<{ active: boolean }>`
  padding: 12px 16px;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  color: ${({ theme, active }) => (active ? theme.primary1 : theme.text1)};
  background-color: ${({ theme, active }) => (active ? theme.bg2 : 'transparent')};
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background-color: ${({ theme }) => theme.bg2};
  }

  &:first-child {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  &:last-child {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`

const languages = [
  { code: 'en', name: 'English' },
  { code: 'ko', name: '한국어' },
  { code: 'fr', name: 'Français' },
  { code: 'ja', name: '日本語' },
  { code: 'zh-TW', name: '繁體中文' },
  { code: 'vi', name: 'Tiếng Việt' },
  { code: 'ar', name: 'العربية' },
  { code: 'de', name: 'Deutsch' }
]

export default function LanguageSelector() {
  const { i18n } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [currentLang, setCurrentLang] = useState<string>('en')
  const containerRef = useRef<HTMLDivElement>(null)

  useOnClickOutside(containerRef, isOpen ? () => setIsOpen(false) : undefined)

  // Update current language when i18n language changes
  useEffect(() => {
    const updateLanguage = () => {
      const lang = (i18n as any).language || (i18n as any).resolvedLanguage || 'en'
      // Normalize language code (e.g., 'en-US' -> 'en')
      const normalizedLang = lang.split('-')[0] === 'zh' ? lang : lang.split('-')[0]
      setCurrentLang(normalizedLang === 'zh' ? 'zh-TW' : normalizedLang)
    }

    // Initial update
    if ((i18n as any).isInitialized) {
      updateLanguage()
    } else {
      ;(i18n as any).on('initialized', updateLanguage)
    }

    // Listen for language changes
    ;(i18n as any).on('languageChanged', updateLanguage)
    ;(i18n as any).on('loaded', updateLanguage)

    return () => {
      ;(i18n as any).off('initialized', updateLanguage)
      ;(i18n as any).off('languageChanged', updateLanguage)
      ;(i18n as any).off('loaded', updateLanguage)
    }
  }, [i18n])

  // Get current language
  const currentLanguage = languages.find(lang => lang.code === currentLang) || languages[0]

  const handleLanguageChange = async (languageCode: string) => {
    try {
      await (i18n as any).changeLanguage(languageCode)
      // Force update by setting state directly
      setCurrentLang(languageCode)
      setIsOpen(false)
      // Force a re-render by triggering a state update
      // This ensures all components using useTranslation will re-render
      window.dispatchEvent(new Event('languagechange'))
    } catch (error) {
      console.error('Error changing language:', error)
    }
  }

  return (
    <LanguageSelectorContainer ref={containerRef}>
      <LanguageButton onClick={() => setIsOpen(!isOpen)}>
        <Globe size={16} />
        <span>{currentLanguage.name}</span>
      </LanguageButton>
      <LanguageMenu isOpen={isOpen}>
        {languages.map(language => (
          <LanguageItem
            key={language.code}
            active={currentLang === language.code}
            onClick={() => handleLanguageChange(language.code)}
          >
            {language.name}
          </LanguageItem>
        ))}
      </LanguageMenu>
    </LanguageSelectorContainer>
  )
}
